import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isFieldVisible } from '../../../utils/formCrafter/visibilityUtils';
import { isFileType } from '../../../utils/formCrafter/fieldTypeUtils';
import { FIELD_TYPES } from '../../../constants/formCrafter.constant';
import {
  validateField,
  validateRepeaterChildren,
} from '../../../utils/formCrafter/formatValidationError';

/**
 * useFormValidation - Hook for managing form validation state and logic
 *
 * This hook handles form validation for all field types including file fields.
 * For file fields, it relies on the `hasFilesForField` callback from `useFileAttachments`
 * which checks server files, staged files (from drafts), and newly uploaded files.
 *
 * @param {Object} schemas - Schema objects by subform ID containing field definitions
 * @param {Function} hasFilesForField - Function to check if file field has files (from useFileAttachments)
 *   - Signature: (subformId, fieldId) => boolean
 *   - Checks: server files, staged files from drafts, local File objects
 * @param {Function} onValidationChange - Callback when validation state changes
 *   - Called with (subformId, { hasError, errorCount, errors })
 * @returns {Object} - Validation state and methods
 */
const useFormValidation = (schemas, hasFilesForField, onValidationChange) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const errorRefs = useRef({});

  const getErrorCount = (subformErrors) =>
    Object.keys(subformErrors || {}).length;

  // Validate a single subform and return errors
  // Uses centralized validateField for DRY, with special handling for:
  // - Hidden fields (skip for applicants)
  // - Conditionally hidden fields
  // - File fields (check server files + new uploads)
  const validateSubform = useCallback(
    (subformId, formData) => {
      const schema = schemas[subformId];
      if (!schema) return {};

      const subformErrors = {};

      schema.fields.forEach((field) => {
        // Skip validation for hidden fields - applicant cannot see or fill them
        if (field.type === FIELD_TYPES.HIDDEN) {
          return;
        }

        // Skip validation for conditionally hidden fields
        if (!isFieldVisible(field, formData)) {
          return;
        }

        const value = formData?.[field.id];
        const customMessage = field.validations?.customMessage;
        const isRequired = field.required || field.validations?.required;

        // Special handling for required file fields
        if (isFileType(field.type) && isRequired) {
          const hasExistingFiles = hasFilesForField(subformId, field.id);
          const hasNewLocalFiles =
            Array.isArray(value) &&
            value.length > 0 &&
            value.some((item) => item instanceof File);

          if (!hasExistingFiles && !hasNewLocalFiles) {
            subformErrors[field.id] =
              customMessage ||
              t('formRenderer.fieldRequired', { field: field.label });
          }
          return; // File fields don't need other validations
        }

        // Use centralized validateField for all other validations
        const error = validateField(value, field, t);
        if (error) {
          subformErrors[field.id] = error;
        }

        // Recursive validation for repeater child fields
        if (
          field.type === FIELD_TYPES.REPEATER &&
          Array.isArray(value) &&
          value.length > 0 &&
          Array.isArray(field.fields)
        ) {
          const childErrors = validateRepeaterChildren(value, field, t);
          Object.assign(subformErrors, childErrors);

          // File fields are skipped in validateRepeaterChildren because their
          // values live in staged/server files, not in row data.  Validate them
          // here using hasFilesForField which checks all file sources.
          const fileChildFields = field.fields.filter(
            (f) => isFileType(f.type) && (f.required || f.validations?.required)
          );
          if (fileChildFields.length > 0) {
            const repeaterLabel = field.label || field.name || field.id;
            value.forEach((row, rowIndex) => {
              if (!row || typeof row !== 'object') return;
              const rowKey =
                row.id !== undefined ? String(row.id) : String(rowIndex);

              fileChildFields.forEach((childField) => {
                const compositeFieldId = `${childField.id}-${rowKey}`;
                const hasExisting = hasFilesForField(
                  subformId,
                  compositeFieldId
                );
                const childValue = row[childField.id];
                const hasNewLocal =
                  Array.isArray(childValue) &&
                  childValue.length > 0 &&
                  childValue.some((item) => item instanceof File);

                if (!hasExisting && !hasNewLocal) {
                  const errorKey = `${field.id}[${rowIndex}].${childField.id}`;
                  const msg =
                    childField.validations?.customMessage ||
                    t('formRenderer.fieldRequired', {
                      field: childField.label,
                    });
                  subformErrors[errorKey] =
                    `${msg} (in "${repeaterLabel}", row ${rowIndex + 1})`;
                }
              });
            });
          }
        }
      });

      return subformErrors;
    },
    [schemas, hasFilesForField, t]
  );

  // Notify parent of validation state changes
  const notifyValidationChange = useCallback(
    (subformId, subformErrors) => {
      if (!onValidationChange) return;

      const errorCount = getErrorCount(subformErrors);
      onValidationChange(subformId, {
        hasError: errorCount > 0,
        errorCount,
        errors: subformErrors,
      });
    },
    [onValidationChange]
  );

  // Touch a single field
  const touchField = useCallback((subformId, fieldId) => {
    setTouchedFields((prev) => ({
      ...prev,
      [subformId]: {
        ...prev[subformId],
        [fieldId]: true,
      },
    }));
  }, []);

  // Touch all fields in a subform (including repeater child fields when formData is provided)
  const touchAllSubformFields = useCallback(
    (subformId, formData) => {
      const schema = schemas[subformId];
      if (!schema) return;

      const fieldsTouched = {};

      const touchRepeaterChildren = (
        repeaterField,
        repeaterValue,
        pathPrefix
      ) => {
        if (
          !Array.isArray(repeaterField.fields) ||
          !Array.isArray(repeaterValue)
        )
          return;
        repeaterValue.forEach((row, rowIndex) => {
          if (!row || typeof row !== 'object') return;
          repeaterField.fields.forEach((childField) => {
            const key = `${pathPrefix}[${rowIndex}].${childField.id}`;
            fieldsTouched[key] = true;
            if (
              childField.type === FIELD_TYPES.REPEATER &&
              Array.isArray(row[childField.id])
            ) {
              touchRepeaterChildren(childField, row[childField.id], key);
            }
          });
        });
      };

      schema.fields.forEach((field) => {
        fieldsTouched[field.id] = true;
        if (
          formData &&
          field.type === FIELD_TYPES.REPEATER &&
          Array.isArray(field.fields)
        ) {
          touchRepeaterChildren(field, formData[field.id], field.id);
        }
      });

      setTouchedFields((prev) => ({
        ...prev,
        [subformId]: { ...prev[subformId], ...fieldsTouched },
      }));
    },
    [schemas]
  );

  // Validate on blur
  const validateOnFieldBlur = useCallback(
    (subformId, formData) => {
      const subformErrors = validateSubform(subformId, formData);
      setErrors((prev) => ({
        ...prev,
        [subformId]: subformErrors,
      }));
      notifyValidationChange(subformId, subformErrors);
      return subformErrors;
    },
    [validateSubform, notifyValidationChange]
  );

  // Validate all subforms
  const validateAllSubforms = useCallback(
    (subforms, formDataBySubform) => {
      const newErrors = {};
      let isValid = true;

      subforms.forEach((subform) => {
        const formData = formDataBySubform[subform.id] || {};
        touchAllSubformFields(subform.id, formData);

        const subformErrors = validateSubform(subform.id, formData);

        if (getErrorCount(subformErrors) > 0) {
          newErrors[subform.id] = subformErrors;
          isValid = false;
        }

        notifyValidationChange(subform.id, subformErrors);
      });

      setErrors(newErrors);
      return { isValid, errors: newErrors };
    },
    [validateSubform, touchAllSubformFields, notifyValidationChange]
  );

  // Scroll to first error
  const scrollToFirstError = useCallback(
    (subforms) => {
      const subformWithError = subforms.find((subform) => errors[subform.id]);
      if (subformWithError && errorRefs.current[subformWithError.id]) {
        errorRefs.current[subformWithError.id].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    },
    [errors]
  );

  // Get validation state for all subforms
  const getValidationState = useCallback(
    (subforms) => {
      const validationState = {};
      subforms.forEach((subform) => {
        const errorCount = getErrorCount(errors[subform.id]);
        validationState[subform.id] = {
          hasError: errorCount > 0,
          errorCount,
        };
      });
      return validationState;
    },
    [errors]
  );

  // Register error ref for a subform
  const registerErrorRef = useCallback((subformId, element, hasErrors) => {
    if (hasErrors) {
      errorRefs.current[subformId] = element;
    }
  }, []);

  // Re-validate only subforms that currently have errors.
  // Used after external data changes (e.g. file upload/delete) to clear
  // stale validation errors without touching clean subforms.
  const revalidateErroredSubforms = useCallback(
    (subforms, formDataBySubform) => {
      subforms.forEach((subform) => {
        if (getErrorCount(errors[subform.id]) > 0) {
          const formData = formDataBySubform[subform.id] || {};
          validateOnFieldBlur(subform.id, formData);
        }
      });
    },
    [errors, validateOnFieldBlur]
  );

  return {
    errors,
    setErrors,
    touchedFields,
    touchField,
    touchAllSubformFields,
    validateSubform,
    validateOnFieldBlur,
    validateAllSubforms,
    revalidateErroredSubforms,
    scrollToFirstError,
    getValidationState,
    registerErrorRef,
  };
};

export default useFormValidation;
